// src/lib/insights.ts
import { QUESTIONS, type Answer } from './questions'
import type { ScoringResult } from './scoring'
import { detectRedFlags } from './redflags'

export interface Insight {
  icon: string
  title: string
  body: string
  questionId: string
}

const INSIGHT_TEMPLATES: Record<string, { icon: string; title: string; bodyTemplate: string }> = {
  'Q1:varies': {
    icon: '🔄',
    title: 'Your unpredictable cycle',
    bodyTemplate: 'You mentioned your periods vary widely in length. This suggests your ovulation may not be occurring consistently — which is the most important fertility health indicator of all. In FEMM medicine, an unpredictable cycle is not a personality trait. It is a signal from the HPO axis.',
  },
  'Q1:long': {
    icon: '📅',
    title: 'Your longer cycles',
    bodyTemplate: 'Cycles longer than 35 days often indicate that ovulation is delayed or not happening. This is one of the most common presentations of PCOS — and it is fully investigable with a targeted hormonal panel.',
  },
  'Q1:very-long': {
    icon: '📅',
    title: 'Your significantly longer cycles',
    bodyTemplate: 'Cycles consistently longer than 45 days are a strong signal of anovulation — a cycle where ovulation is not occurring. This is the most common presentation of PCOS and requires targeted hormonal investigation.',
  },
  'Q6:assume': {
    icon: '💡',
    title: 'The ovulation assumption',
    bodyTemplate: 'You assumed you ovulate because your periods come. This is the single most important insight from the quiz: regular periods do not confirm regular ovulation. A woman can menstruate without ovulating — and the health implications are significant.',
  },
  'Q6:unsure': {
    icon: '💡',
    title: 'What you don\'t know about your cycle',
    bodyTemplate: 'Not knowing whether you ovulate regularly is more common than you think — and it matters. In FEMM medicine, ovulation is the event that drives progesterone production, which regulates mood, sleep, energy, and long-term hormonal health.',
  },
  'Q7:jaw': {
    icon: '🔬',
    title: 'Where your acne appears matters',
    bodyTemplate: 'You described acne on your jaw and chin area. In endocrinology, this specific location is driven by androgen receptor activity — not general skincare issues. It is a recognisable signal of possible PCOS or adrenal androgen excess.',
  },
  'Q10:belly': {
    icon: '⚖️',
    title: 'Your weight pattern',
    bodyTemplate: 'Weight gain concentrated around your belly and waist is the most clinically significant pattern for reproductive health. It reflects how your cells are responding to insulin — and insulin directly affects ovulation.',
  },
  'Q10:cant-lose': {
    icon: '⚖️',
    title: 'The weight that won\'t shift',
    bodyTemplate: 'When weight resists all reasonable efforts to lose it, the most common overlooked cause is metabolic dysregulation — either insulin resistance or thyroid dysfunction. Both are measurable and treatable.',
  },
  'Q11:once-twice': {
    icon: '🩺',
    title: 'A symptom rarely discussed',
    bodyTemplate: 'Nipple discharge outside pregnancy or breastfeeding is one of the most specific symptoms of a treatable hormonal condition called hyperprolactinemia. It affects 9–17% of women with reproductive disorders — and many never mention it.',
  },
  'Q11:occasional': {
    icon: '🩺',
    title: 'A symptom rarely discussed',
    bodyTemplate: 'Nipple discharge outside pregnancy or breastfeeding is one of the most specific symptoms of a treatable hormonal condition called hyperprolactinemia. It affects 9–17% of women with reproductive disorders — and many never mention it.',
  },
  'Q11:regularly': {
    icon: '🩺',
    title: 'An important clinical signal',
    bodyTemplate: 'Regular nipple discharge is a near-certain signal of elevated prolactin — a common, treatable condition that directly suppresses ovulation. A single blood test (fasting morning prolactin) can confirm this.',
  },
  'Q12:afternoon-crash': {
    icon: '⚡',
    title: 'Your afternoon energy crashes',
    bodyTemplate: 'Afternoon energy crashes — especially after meals — are one of the clearest behavioural signs of insulin resistance. Your cells are not taking up glucose efficiently, creating a sensation of energy deficit that your brain interprets as fatigue and carbohydrate craving.',
  },
  'Q12:depleted': {
    icon: '⚡',
    title: 'Your energy depletion',
    bodyTemplate: 'Severe, pervasive fatigue that is unresponsive to rest is a key indicator of thyroid dysfunction. When thyroid hormones are insufficient, every cell in the body slows down — including your brain. This is not a lifestyle problem. It is a biochemical one.',
  },
  'Q13:daily': {
    icon: '🍬',
    title: 'Your carbohydrate cravings',
    bodyTemplate: 'Daily afternoon cravings for sweets and carbs are not a willpower problem. They are a physiological signal: when cells resist insulin, the brain craves fast energy. This is a recognisable, treatable metabolic pattern.',
  },
  'Q13:compulsive': {
    icon: '🍬',
    title: 'Your compulsive cravings',
    bodyTemplate: 'Cravings that feel compulsive — beyond appetite, almost irresistible — are a strong marker of insulin resistance. The reward pathway in the brain becomes dysregulated when glucose is not being effectively taken up by cells.',
  },
  'Q15:severe': {
    icon: '🧠',
    title: 'Your premenstrual experience',
    bodyTemplate: 'Severe premenstrual mood changes are not a personality flaw. Progesterone — the hormone produced after ovulation — has a direct calming effect on the brain via GABA receptors. When progesterone is low or absent, this calming effect disappears. The mood shift has a hormonal mechanism.',
  },
  'Q15:extreme': {
    icon: '🧠',
    title: 'The hardest week of your month',
    bodyTemplate: 'When the week before your period is reliably the most difficult week — every month — this is a clinical pattern, not a personal failing. It is consistent with PMDD, which has a specific hormonal mechanism: the absence of adequate progesterone-derived allopregnanolone, which normally calms the nervous system.',
  },
  'Q16:struggling': {
    icon: '🧠',
    title: 'Your emotional wellbeing',
    bodyTemplate: 'A 2024 study (del Río et al.) found that 52.6% of young women with confirmed ovulatory dysfunction scored for clinical depression — and 71% reported elevated tension. Your emotional experience may have a hormonal explanation that has never been investigated.',
  },
  'Q16:frequently-struggling': {
    icon: '🧠',
    title: 'Your emotional baseline',
    bodyTemplate: 'When anxiety, irritability, or low mood has become your baseline rather than an occasional experience, it is worth investigating whether there is a hormonal driver. Thyroid dysfunction, low progesterone, and elevated androgens all have measurable effects on brain chemistry.',
  },
  'Q18:significantly-lower': {
    icon: '💎',
    title: 'Your changing self-esteem',
    bodyTemplate: 'The same 2024 study found that self-esteem was negatively correlated with DHEAS levels and insulin resistance. The sense of becoming "less than you were" may not be a personal failing — it may be a hormonal process that is measurable and treatable.',
  },
  'Q18:inadequacy': {
    icon: '💎',
    title: 'The feeling of being less',
    bodyTemplate: 'A persistent sense of inadequacy — especially when it feels disconnected from your circumstances — has been linked in published research (del Río et al., 2024) to elevated DHEAS and insulin resistance. This is a physiological finding, not a reflection of your character.',
  },
  'Q4:debilitating': {
    icon: '⚠️',
    title: 'Debilitating period pain is a clinical signal',
    bodyTemplate: 'Period pain so severe that it prevents you from working or moving is not a normal feature of menstruation. It is one of the most reliable early signals of endometriosis — a condition that currently takes an average of 7–10 years to diagnose because this symptom is routinely dismissed.',
  },
  'Q5:absent-6m-plus': {
    icon: '⚠️',
    title: 'An absent period is a clinical event',
    bodyTemplate: 'An absent period for 6+ months is not a benign finding. It is a signal of significant HPG axis disruption. The most common treatable cause is elevated prolactin — a hormonal condition identified with a single blood test.',
  },
  'Q23:told-normal': {
    icon: '🤝',
    title: 'You were told this was normal',
    bodyTemplate: "You mentioned that when you raised these symptoms with a doctor, you were told they were normal. FEMM medicine disagrees. The symptoms you described are not 'just how women are.' They are signals from identifiable hormonal systems — and they deserve investigation.",
  },
  'Q23:offered-pill': {
    icon: '💊',
    title: 'Beyond the pill',
    bodyTemplate: 'You were offered the pill as a solution for your symptoms. The pill can manage symptoms effectively — but it does so by overriding your natural hormonal cycle, not by identifying and treating the underlying cause. A FEMM consultation investigates what the pill is masking.',
  },
  'Q23:told-lose-weight': {
    icon: '🤝',
    title: 'Your experience was real',
    bodyTemplate: "Being told to 'lose weight' or 'manage stress' when you're experiencing hormonal symptoms is one of the most common forms of medical dismissal for women. Weight gain and stress can be symptoms of hormonal disruption — not just causes. FEMM investigates the direction of that relationship.",
  },
}

export function generateInsights(answers: Answer[], _scores: ScoringResult): Insight[] {
  const redFlagAnswers = new Set(
    detectRedFlags(answers).reasons.map(r => r.questionId)
  )

  const candidates: { insight: Insight; priority: number }[] = []

  for (const answer of answers) {
    const key = `${answer.questionId}:${answer.answerKey}`
    const template = INSIGHT_TEMPLATES[key]
    if (!template) continue

    const question = QUESTIONS.find(q => q.id === answer.questionId)
    const option = question?.options.find(o => o.key === answer.answerKey)
    const score = option?.score || 0
    const isRedFlag = redFlagAnswers.has(answer.questionId)

    candidates.push({
      insight: {
        icon: template.icon,
        title: template.title,
        body: template.bodyTemplate,
        questionId: answer.questionId,
      },
      priority: score * 10 + (isRedFlag ? 100 : 0),
    })
  }

  // Include Q23 context insights with elevated priority
  const q23 = answers.find(a => a.questionId === 'Q23')
  if (q23) {
    const template = INSIGHT_TEMPLATES[`Q23:${q23.answerKey}`]
    if (template) {
      candidates.push({
        insight: {
          icon: template.icon,
          title: template.title,
          body: template.bodyTemplate,
          questionId: 'Q23',
        },
        priority: 50,
      })
    }
  }

  candidates.sort((a, b) => b.priority - a.priority)
  return candidates.slice(0, 3).map(c => c.insight)
}
